import { type ReactNode, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { TotpEnrollmentDialog } from '@/components/shared/totp-enrollment-dialog';
import { Spinner } from '@/components/ui/spinner';
import { getModuleAccessPermissions } from '@/lib/module-access';
import { hasAnyPermission, hasPermission } from '@/lib/permissions';
import { isAccountPath, needsTotpEnrollment } from '@/lib/totp-enrollment';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore, type SystemRole } from '@/stores/church-store';

const FullScreenLoader = () => (
  <div className="flex min-h-screen items-center justify-center" role="status">
    <Spinner className="size-8 text-brand" />
  </div>
);

/**
 * Ensures a church is selected for console work.
 *
 * No church selected (including zero memberships) → picker. Create and select
 * routes live outside this guard.
 */
export const RequireChurch = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('security');
  const location = useLocation();
  const status = useChurchStore((state) => state.status);
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const restore = useChurchStore((state) => state.restore);
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);
  const user = useAuthStore((state) => state.user);
  const openSupportSession = useChurchStore((state) => state.openSupportSession);
  const supportChurchId = user?.supportAccess?.churchId ?? null;

  const onAccount = isAccountPath(location.pathname);
  const mustEnrollTotp = needsTotpEnrollment(church, user);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  useEffect(() => {
    if (!supportChurchId || church?.id === supportChurchId) return;
    void openSupportSession(supportChurchId);
  }, [church?.id, openSupportSession, supportChurchId]);

  if (status === 'unknown' || (supportChurchId && church?.id !== supportChurchId)) {
    return <FullScreenLoader />;
  }

  if (!church) {
    return <Navigate to="/churches/select" replace />;
  }

  if (mustEnrollTotp && !onAccount) {
    return (
      <TotpEnrollmentDialog
        open
        dismissible={false}
        autoStart
        description={t('enrollmentRequired.churchPolicy')}
        onEnrolled={() => {
          void refreshCurrent();
        }}
        onOpenChange={() => {}}
      />
    );
  }

  // Church id is chosen before membership and enabled modules finish loading. Rendering
  // console routes in that gap makes module gates lie — e.g. Offerings shows "Turn on
  // Programs" right after sign-in even when Programs is already on.
  if (!membership && !onAccount) return <FullScreenLoader />;

  return <>{children}</>;
};

const roleRank = (role: SystemRole): number => {
  switch (role) {
    case 'owner':
      return 3;
    case 'admin':
      return 2;
    case 'staff':
      return 1;
    default:
      return 0;
  }
};

export const RequireAdminOrOwner = ({ children }: { children: ReactNode }) => {
  const membership = useChurchStore((state) => state.membership);
  const location = useLocation();

  if (!membership) return <FullScreenLoader />;

  if (roleRank(membership.systemRole) < 2) {
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const RequirePermission = ({
  children,
  permission,
}: {
  children: ReactNode;
  permission: string;
}) => {
  const membership = useChurchStore((state) => state.membership);
  const location = useLocation();

  if (!membership) return <FullScreenLoader />;

  const allowed =
    membership.systemRole === 'owner' ||
    membership.systemRole === 'admin' ||
    membership.permissions.includes(permission);

  if (!allowed) return <Navigate to="/home" state={{ from: location }} replace />;
  return <>{children}</>;
};

export const RequireAnyPermission = ({
  children,
  permissions,
}: {
  children: ReactNode;
  permissions: readonly string[];
}) => {
  const membership = useChurchStore((state) => state.membership);
  const location = useLocation();

  if (!membership) return <FullScreenLoader />;

  const allowed =
    membership.systemRole === 'owner' ||
    membership.systemRole === 'admin' ||
    permissions.some((code) => membership.permissions.includes(code));

  if (!allowed) return <Navigate to="/home" state={{ from: location }} replace />;
  return <>{children}</>;
};

export const RequirePlatformAdmin = ({ children }: { children: ReactNode }) => {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (user?.platformRole !== 'platform_admin') {
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const RequirePlatformManager = ({ children }: { children: ReactNode }) => {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  const allowed =
    user?.platformRole === 'platform_admin' || user?.platformRole === 'platform_manager';

  if (!allowed) return <Navigate to="/home" state={{ from: location }} replace />;
  return <>{children}</>;
};

/** Hides a route when the church has not enabled the module. */
export const RequireModule = ({
  children,
  module: moduleCode,
}: {
  children: ReactNode;
  module: string;
}) => {
  const enabled = useChurchStore((state) => state.isModuleEnabled(moduleCode));
  const location = useLocation();

  if (!enabled) return <Navigate to="/home" state={{ from: location }} replace />;
  return <>{children}</>;
};

const useModuleAccessAllowed = (
  moduleCode: string,
  permission?: string,
  permissions?: readonly string[],
): boolean | null => {
  const membership = useChurchStore((state) => state.membership);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const location = useLocation();

  if (!membership) return null;

  if (membership.systemRole === 'congregant') {
    return location.pathname === '/home' || location.pathname === '/';
  }

  if (!isModuleEnabled(moduleCode)) return false;

  if (permission) {
    return hasPermission(membership, permission);
  }
  if (permissions && permissions.length > 0) {
    return hasAnyPermission(membership, permissions);
  }
  const accessPermissions = getModuleAccessPermissions(moduleCode);
  if (accessPermissions) {
    return hasAnyPermission(membership, accessPermissions);
  }
  return true;
};

/** Layout guard: module enabled plus view permission (or explicit permission / any-of list). */
export const RequireModuleAccessLayout = ({
  module: moduleCode,
  permission,
  permissions,
}: {
  module: string;
  permission?: string;
  permissions?: readonly string[];
}) => {
  const location = useLocation();
  const allowed = useModuleAccessAllowed(moduleCode, permission, permissions);

  if (allowed === null) return <FullScreenLoader />;
  if (!allowed) return <Navigate to="/home" state={{ from: location }} replace />;
  return <Outlet />;
};

/** Element guard: module enabled plus view permission (or explicit permission / any-of list). */
export const RequireModuleAccess = ({
  children,
  module: moduleCode,
  permission,
  permissions,
}: {
  children: ReactNode;
  module: string;
  permission?: string;
  permissions?: readonly string[];
}) => {
  const location = useLocation();
  const allowed = useModuleAccessAllowed(moduleCode, permission, permissions);

  if (allowed === null) return <FullScreenLoader />;
  if (!allowed) return <Navigate to="/home" state={{ from: location }} replace />;
  return <>{children}</>;
};
